import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  FileDown, 
  Globe, 
  TrendingUp, 
  TrendingDown,
  Users, 
  Trophy,
  Calendar,
  Target,
  Sparkles,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  BookOpen,
  Star,
  ArrowRight,
  Shield,
  Zap,
  Layers
} from 'lucide-react';
import { useDemoMode } from '@/components/common/DemoModeToggle';
import { DataIntegrityBadge, LastUpdated, PlatformFooter } from '@/components/common/PageElements';
import { generatePDFReport } from '@/services/pdfExportService';
import { DecisionInsightPanel } from '@/components/common/DecisionInsightPanel';
import { DashboardTabs } from '@/components/common/DashboardTabs';
import { FeedbackWidget } from '@/components/common/FeedbackWidget';
import { trackEvent } from '@/hooks/useAnalytics';
import { authFetch } from '@/utils/authFetch';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Utility functions
const formatCurrency = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(0)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};

const formatNumber = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value?.toString() || '0';
};

// Team Summary Card Component
const TeamSummaryCard = ({ team, isHighlighted }) => {
  if (!team) return null;
  
  const eco = team.economic_value || {};
  const brand = team.brand_metrics || {};
  
  return (
    <Card className={`bg-card border-border ${isHighlighted ? 'ring-2 ring-primary/50' : ''}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-foreground">{team.short_name}</h3>
            <p className="text-sm text-muted-foreground">{team.name}</p>
          </div>
          <Badge className="bg-primary/10 text-primary border-primary/30">
            FIFA #{team.fifa_ranking}
          </Badge>
        </div>
        
        <div className="space-y-4">
          <div>
            <p className="text-2xs text-muted-foreground uppercase tracking-wide mb-1">Brand Value Range</p>
            <p className="text-2xl font-bold text-primary">
              {formatCurrency(eco.range_low)} – {formatCurrency(eco.range_high)}
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-muted/30 text-center">
              <p className="text-xl font-bold text-foreground">{brand.overall_brand_score || 0}</p>
              <p className="text-2xs text-muted-foreground">Overall Score</p>
            </div>
            <div className="p-3 rounded-lg bg-stable/10 text-center">
              <p className="text-xl font-bold text-stable">+{eco.year_over_year_change || 0}%</p>
              <p className="text-2xs text-muted-foreground">YoY Growth</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Cycle Progress</span>
            <span className="font-medium">{team.current_cycle?.cycle_progress_pct || 0}%</span>
          </div>
          <Progress value={team.current_cycle?.cycle_progress_pct || 0} className="h-2" />
        </div>
      </CardContent>
    </Card>
  );
};

// Dual Cycle Chart Component
const DualCycleChart = ({ usmnt, uswnt, selectedCycle, onCycleChange }) => {
  const cycles = [
    { id: 'men', label: 'Men 2022–2026', team: 'USMNT' },
    { id: 'women', label: 'Women 2023–2027', team: 'USWNT' },
  ];
  
  const getTeamData = (team, historical, current) => {
    const dataPoints = [];
    
    if (historical && historical.length > 0) {
      historical.forEach(cycle => {
        const startYear = parseInt(cycle.years.split('-')[0]);
        const endYear = parseInt(cycle.years.split('-')[1]);
        dataPoints.push({ year: startYear, score: cycle.brand_index_start });
        dataPoints.push({ year: endYear, score: cycle.brand_index_end });
      });
    }
    
    // Add current score
    const currentScore = team === 'usmnt' 
      ? (usmnt?.brand_metrics?.overall_brand_score || 71)
      : (uswnt?.brand_metrics?.overall_brand_score || 77);
    dataPoints.push({ year: 2026, score: currentScore });
    
    return dataPoints.sort((a, b) => a.year - b.year);
  };
  
  const usmntData = getTeamData('usmnt', usmnt?.historical_cycles, usmnt?.current_cycle);
  const uswntData = getTeamData('uswnt', uswnt?.historical_cycles, uswnt?.current_cycle);
  
  // Fixed SVG dimensions for proper rendering (matching CycleEvolutionChart)
  const svgWidth = 600;
  const svgHeight = 260;
  const padding = { top: 30, right: 40, bottom: 45, left: 55 };
  const chartWidth = svgWidth - padding.left - padding.right;
  const chartHeight = svgHeight - padding.top - padding.bottom;
  
  const allData = [...usmntData, ...uswntData];
  const minYear = Math.min(...allData.map(d => d.year));
  const maxYear = Math.max(...allData.map(d => d.year));
  const allScores = allData.map(d => d.score);
  const minScore = Math.floor((Math.min(...allScores) - 5) / 10) * 10;
  const maxScore = Math.ceil((Math.max(...allScores) + 5) / 10) * 10;
  const yearRange = maxYear - minYear || 1;
  const scoreRange = maxScore - minScore || 1;
  
  // Scale functions
  const scaleX = (year) => padding.left + ((year - minYear) / yearRange) * chartWidth;
  const scaleY = (score) => padding.top + chartHeight - ((score - minScore) / scoreRange) * chartHeight;
  
  // Generate Y-axis ticks
  const yTicks = [];
  for (let score = minScore; score <= maxScore; score += 10) {
    yTicks.push(score);
  }
  
  // Generate X-axis ticks
  const xTicks = [2018, 2020, 2022, 2024, 2026].filter(y => y >= minYear && y <= maxYear);
  
  const generatePath = (data, color) => {
    if (data.length < 2) return null;
    
    const path = data.map((point, i) => {
      const x = scaleX(point.year);
      const y = scaleY(point.score);
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
    
    return (
      <path 
        d={path} 
        fill="none" 
        stroke={color} 
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  };
  
  return (
    <Card className="bg-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            World Cup Cycle Intelligence
          </CardTitle>
          <div className="flex gap-2">
            {cycles.map(cycle => (
              <Button
                key={cycle.id}
                variant={selectedCycle === cycle.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => onCycleChange(cycle.id)}
                className="text-xs"
              >
                {cycle.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="bg-muted/20 rounded-lg p-4 mb-4">
          <div className="w-full overflow-hidden" style={{ minHeight: '260px' }}>
            <svg 
              viewBox={`0 0 ${svgWidth} ${svgHeight}`} 
              className="w-full h-auto"
              preserveAspectRatio="xMidYMid meet"
              style={{ maxHeight: '300px' }}
            >
              {/* Background */}
              <rect 
                x={padding.left} 
                y={padding.top} 
                width={chartWidth} 
                height={chartHeight} 
                fill="transparent"
              />
              
              {/* Grid lines - horizontal */}
              {yTicks.map(score => {
                const y = scaleY(score);
                return (
                  <g key={`grid-${score}`}>
                    <line 
                      x1={padding.left} 
                      y1={y} 
                      x2={svgWidth - padding.right} 
                      y2={y} 
                      stroke="currentColor" 
                      strokeOpacity="0.1"
                      strokeDasharray="4,4"
                    />
                  </g>
                );
              })}
              
              {/* Grid lines - vertical */}
              {xTicks.map(year => {
                const x = scaleX(year);
                return (
                  <line 
                    key={`vgrid-${year}`}
                    x1={x} 
                    y1={padding.top} 
                    x2={x} 
                    y2={svgHeight - padding.bottom} 
                    stroke="currentColor" 
                    strokeOpacity="0.05"
                  />
                );
              })}
              
              {/* Y-axis labels */}
              {yTicks.map(score => {
                const y = scaleY(score);
                return (
                  <text 
                    key={`ylabel-${score}`}
                    x={padding.left - 12} 
                    y={y + 4} 
                    fontSize="12" 
                    fontFamily="system-ui, sans-serif"
                    fill="currentColor" 
                    opacity="0.6"
                    textAnchor="end"
                  >
                    {score}
                  </text>
                );
              })}
              
              {/* X-axis labels */}
              {xTicks.map(year => {
                const x = scaleX(year);
                return (
                  <text 
                    key={`xlabel-${year}`}
                    x={x} 
                    y={svgHeight - padding.bottom + 22} 
                    fontSize="12" 
                    fontFamily="system-ui, sans-serif"
                    fill="currentColor" 
                    opacity="0.6"
                    textAnchor="middle"
                  >
                    {year}
                  </text>
                );
              })}
              
              {/* Y-axis title */}
              <text 
                x={14} 
                y={svgHeight / 2} 
                fontSize="11" 
                fontFamily="system-ui, sans-serif"
                fill="currentColor" 
                opacity="0.5"
                textAnchor="middle"
                transform={`rotate(-90, 14, ${svgHeight / 2})`}
              >
                Brand Index
              </text>
              
              {/* X-axis title */}
              <text 
                x={svgWidth / 2} 
                y={svgHeight - 6} 
                fontSize="11" 
                fontFamily="system-ui, sans-serif"
                fill="currentColor" 
                opacity="0.5"
                textAnchor="middle"
              >
                Year
              </text>
              
              {/* Gradient definitions */}
              <defs>
                <linearGradient id="usmntGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="1" />
                </linearGradient>
                <linearGradient id="uswntGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="hsl(var(--stable-green))" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="hsl(var(--stable-green))" stopOpacity="1" />
                </linearGradient>
              </defs>
              
              {/* Lines */}
              {generatePath(usmntData, 'hsl(var(--primary))')}
              {generatePath(uswntData, 'hsl(var(--stable-green))')}
              
              {/* USMNT Data points */}
              {usmntData.map((point, i) => {
                const x = scaleX(point.year);
                const y = scaleY(point.score);
                const isLast = i === usmntData.length - 1;
                return (
                  <g key={`usmnt-point-${i}`}>
                    {isLast && (
                      <circle cx={x} cy={y} r="10" fill="hsl(var(--primary))" opacity="0.2" />
                    )}
                    <circle 
                      cx={x} cy={y} 
                      r={isLast ? 6 : 4} 
                      fill="hsl(var(--primary))"
                      stroke="hsl(var(--background))"
                      strokeWidth="2"
                    />
                    {isLast && (
                      <g>
                        <rect x={x - 16} y={y - 28} width="32" height="18" rx="4" fill="hsl(var(--primary))" />
                        <text x={x} y={y - 15} fontSize="11" fontFamily="system-ui" fontWeight="bold" fill="white" textAnchor="middle">
                          {point.score}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
              
              {/* USWNT Data points */}
              {uswntData.map((point, i) => {
                const x = scaleX(point.year);
                const y = scaleY(point.score);
                const isLast = i === uswntData.length - 1;
                return (
                  <g key={`uswnt-point-${i}`}>
                    {isLast && (
                      <circle cx={x} cy={y} r="10" fill="hsl(var(--stable-green))" opacity="0.2" />
                    )}
                    <circle 
                      cx={x} cy={y} 
                      r={isLast ? 6 : 4} 
                      fill="hsl(var(--stable-green))"
                      stroke="hsl(var(--background))"
                      strokeWidth="2"
                    />
                    {isLast && (
                      <g>
                        <rect x={x - 16} y={y - 28} width="32" height="18" rx="4" fill="hsl(var(--stable-green))" />
                        <text x={x} y={y - 15} fontSize="11" fontFamily="system-ui" fontWeight="bold" fill="white" textAnchor="middle">
                          {point.score}
                        </text>
                      </g>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>
          
          {/* Legend */}
          <div className="flex justify-center gap-6 mt-3 pt-3 border-t border-border/50">
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-primary rounded" />
              <span className="text-xs text-muted-foreground">USMNT ({usmnt?.brand_metrics?.overall_brand_score || 71})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-stable rounded" />
              <span className="text-xs text-muted-foreground">USWNT ({uswnt?.brand_metrics?.overall_brand_score || 77})</span>
            </div>
          </div>
        </div>
        
        {/* Milestones */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary" />
              USMNT Milestones
            </h4>
            <div className="space-y-1">
              {(usmnt?.current_cycle?.key_tournaments || []).slice(0, 4).map((t, i) => (
                <div key={i} className="flex justify-between text-xs p-2 rounded bg-muted/20">
                  <span>{t.name}</span>
                  <Badge variant="outline" className="text-2xs">{t.result}</Badge>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-stable" />
              USWNT Milestones
            </h4>
            <div className="space-y-1">
              {(uswnt?.current_cycle?.key_tournaments || []).slice(0, 4).map((t, i) => (
                <div key={i} className="flex justify-between text-xs p-2 rounded bg-muted/20">
                  <span>{t.name}</span>
                  <Badge variant="outline" className="text-2xs">{t.result}</Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Digital Presence Comparison Component with Verified Data Support
const DigitalPresenceComparison = ({ usmnt, uswnt, verifiedData, isDemoMode }) => {
  const platforms = [
    { key: 'instagram', label: 'Instagram', icon: '📸' },
    { key: 'twitter', label: 'Twitter/X', icon: '𝕏' },
    { key: 'tiktok', label: 'TikTok', icon: '🎵' },
    { key: 'youtube', label: 'YouTube', icon: '▶️' },
  ];
  
  // Get follower data - prefer verified data when demo mode is OFF
  const getFollowers = (teamId, platform) => {
    // If demo mode is ON, use simulated data
    if (isDemoMode) {
      const team = teamId === 'usmnt' ? usmnt : uswnt;
      const digital = team?.digital_presence || {};
      if (platform === 'youtube') return { value: digital.youtube?.subscribers || 0, source: 'SIMULATED' };
      return { value: digital[platform]?.followers || 0, source: 'SIMULATED' };
    }
    
    // Use verified data when available
    const teamVerified = verifiedData?.teams?.[teamId]?.platforms?.[platform];
    if (teamVerified) {
      const value = teamVerified.followers || teamVerified.subscribers || 0;
      return { 
        value, 
        source: teamVerified.source || 'ESTIMATED',
        verified_at: teamVerified.verified_at,
        handle: teamVerified.handle,
      };
    }
    
    // Fallback to simulated
    const team = teamId === 'usmnt' ? usmnt : uswnt;
    const digital = team?.digital_presence || {};
    if (platform === 'youtube') return { value: digital.youtube?.subscribers || 0, source: 'SIMULATED' };
    return { value: digital[platform]?.followers || 0, source: 'SIMULATED' };
  };
  
  // Get total reach
  const getTotalReach = (teamId) => {
    if (isDemoMode) {
      const team = teamId === 'usmnt' ? usmnt : uswnt;
      return { value: team?.digital_presence?.total_social_reach || 0, source: 'SIMULATED' };
    }
    
    const teamVerified = verifiedData?.teams?.[teamId];
    if (teamVerified) {
      return { 
        value: teamVerified.total_reach || 0, 
        source: teamVerified.total_reach_source || 'CALCULATED',
      };
    }
    
    const team = teamId === 'usmnt' ? usmnt : uswnt;
    return { value: team?.digital_presence?.total_social_reach || 0, source: 'SIMULATED' };
  };
  
  const allValues = platforms.flatMap(p => [
    getFollowers('usmnt', p.key).value,
    getFollowers('uswnt', p.key).value
  ]);
  const maxFollowers = Math.max(...allValues);
  
  // Get data mode info
  const dataMode = verifiedData?.data_mode || 'DEMO';
  const lastUpdated = verifiedData?.teams?.usmnt?.last_updated || verifiedData?.teams?.uswnt?.last_updated;
  
  // Format timestamp
  const formatTimestamp = (ts) => {
    if (!ts) return null;
    const date = new Date(ts);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Source badge color
  const getSourceBadge = (source) => {
    switch (source) {
      case 'VERIFIED':
        return <Badge className="bg-stable/20 text-stable border-stable/30 text-2xs"><CheckCircle2 className="w-2.5 h-2.5 mr-1" />Verified</Badge>;
      case 'ESTIMATED':
        return <Badge className="bg-warning/20 text-warning border-warning/30 text-2xs">Est.</Badge>;
      case 'SIMULATED':
      default:
        return <Badge className="bg-muted/40 text-muted-foreground border-muted text-2xs">Demo</Badge>;
    }
  };
  
  return (
    <Card className="bg-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Digital Presence Comparison
          </CardTitle>
          {!isDemoMode && (
            <div className="flex items-center gap-2">
              {dataMode === 'VERIFIED' || dataMode === 'PARTIAL' ? (
                <Badge className="bg-stable/10 text-stable border-stable/30">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {dataMode === 'VERIFIED' ? 'Verified Data' : 'Partial Verified'}
                </Badge>
              ) : (
                <Badge className="bg-muted/40 text-muted-foreground">Simulated</Badge>
              )}
            </div>
          )}
        </div>
        {!isDemoMode && lastUpdated && (
          <p className="text-xs text-muted-foreground mt-1">
            Last updated: {formatTimestamp(lastUpdated)}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {platforms.map(platform => {
            const usmntData = getFollowers('usmnt', platform.key);
            const uswntData = getFollowers('uswnt', platform.key);
            const usmntPct = maxFollowers > 0 ? (usmntData.value / maxFollowers) * 100 : 0;
            const uswntPct = maxFollowers > 0 ? (uswntData.value / maxFollowers) * 100 : 0;
            
            return (
              <div key={platform.key} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{platform.label}</span>
                  {!isDemoMode && (usmntData.source === 'VERIFIED' || uswntData.source === 'VERIFIED') && (
                    <div className="flex gap-1">
                      {usmntData.source === 'VERIFIED' && getSourceBadge('VERIFIED')}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span className="flex items-center gap-1">
                        USMNT
                        {!isDemoMode && usmntData.handle && (
                          <span className="text-primary/60">@{usmntData.handle}</span>
                        )}
                      </span>
                      <span className="font-medium text-foreground">{formatNumber(usmntData.value)}</span>
                    </div>
                    <div className="h-3 bg-muted/30 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${usmntPct}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span className="flex items-center gap-1">
                        USWNT
                        {!isDemoMode && uswntData.handle && (
                          <span className="text-stable/60">@{uswntData.handle}</span>
                        )}
                      </span>
                      <span className="font-medium text-foreground">{formatNumber(uswntData.value)}</span>
                    </div>
                    <div className="h-3 bg-muted/30 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-stable rounded-full transition-all"
                        style={{ width: `${uswntPct}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Total Reach */}
        <div className="grid grid-cols-2 gap-4 mt-6 pt-4 border-t border-border">
          <div className="p-4 rounded-lg bg-primary/10 text-center">
            <p className="text-2xl font-bold text-primary">
              {formatNumber(getTotalReach('usmnt').value)}
            </p>
            <p className="text-xs text-muted-foreground">USMNT Total Reach</p>
          </div>
          <div className="p-4 rounded-lg bg-stable/10 text-center">
            <p className="text-2xl font-bold text-stable">
              {formatNumber(getTotalReach('uswnt').value)}
            </p>
            <p className="text-xs text-muted-foreground">USWNT Total Reach</p>
          </div>
        </div>
        
        {/* Data Source Footer */}
        {!isDemoMode && (
          <div className="mt-4 pt-3 border-t border-border/50 text-xs text-muted-foreground">
            <div className="flex items-center gap-2 flex-wrap">
              <span>Data source:</span>
              {getSourceBadge(dataMode === 'VERIFIED' ? 'VERIFIED' : dataMode === 'PARTIAL' ? 'VERIFIED' : 'SIMULATED')}
              <span className="text-muted-foreground/60">
                Instagram & X/Twitter verified from official accounts
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Star Players Comparison Component
const StarPlayersComparison = ({ usmnt, uswnt }) => {
  const PlayerList = ({ players, teamColor }) => (
    <div className="space-y-2">
      {(players || []).slice(0, 5).map((player, i) => (
        <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-muted/20">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${teamColor}`}>
            {i + 1}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{player.name}</p>
            <p className="text-2xs text-muted-foreground">{formatNumber(player.instagram_followers)} followers</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold">{player.brand_score}</p>
            <p className="text-2xs text-muted-foreground">Score</p>
          </div>
        </div>
      ))}
    </div>
  );
  
  return (
    <Card className="bg-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="w-5 h-5" />
          Top Players by Brand Score
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary" />
              USMNT
            </h4>
            <PlayerList 
              players={usmnt?.roster_brand_signals?.star_players} 
              teamColor="bg-primary/20 text-primary"
            />
          </div>
          <div>
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-stable" />
              USWNT
            </h4>
            <PlayerList 
              players={uswnt?.roster_brand_signals?.star_players} 
              teamColor="bg-stable/20 text-stable"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Strategic Insights Component
const StrategicInsights = ({ usmnt, uswnt }) => {
  const insights = [
    {
      icon: TrendingUp,
      color: 'text-stable',
      bgColor: 'bg-stable/10',
      title: 'USMNT Brand Growth',
      text: `USMNT brand growing +${usmnt?.economic_value?.year_over_year_change || 18}% YoY driven by Nations League success and 2026 World Cup host momentum.`
    },
    {
      icon: Trophy,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      title: 'USWNT Global Leadership',
      text: `USWNT retains world #${uswnt?.fifa_ranking || 1} status with high digital reach (${formatNumber(uswnt?.digital_presence?.total_social_reach)}) but slower growth trajectory post-Paris 2024.`
    },
    {
      icon: Target,
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      title: 'Sponsorship Leverage',
      text: 'Sponsorship leverage opportunities ahead of 2026/2027 World Cup cycles. Combined federation brand value exceeds $400M with dual tournament exposure.'
    },
    {
      icon: Zap,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      title: 'Host Premium Impact',
      text: 'Co-host status for FIFA World Cup 2026 projected to add $50-65M to USMNT brand value through increased domestic exposure and merchandise revenue.'
    }
  ];
  
  return (
    <Card className="bg-card border-l-4 border-l-stable">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Strategic Insights
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          {insights.map((insight, i) => {
            const Icon = insight.icon;
            return (
              <div key={i} className={`p-4 rounded-lg ${insight.bgColor}`}>
                <div className="flex items-start gap-3">
                  <Icon className={`w-5 h-5 ${insight.color} mt-0.5`} />
                  <div>
                    <p className="font-medium text-foreground text-sm mb-1">{insight.title}</p>
                    <p className="text-sm text-muted-foreground">{insight.text}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Collapsible Methodology Section
const MethodologySection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const weights = [
    { label: 'Social Media', value: 20 },
    { label: 'Roster Brand Power', value: 25 },
    { label: 'Tournament Performance', value: 20 },
    { label: 'Commercial Strength', value: 20 },
    { label: 'Market Factors', value: 15 },
  ];
  
  return (
    <Card className="bg-card">
      <CardHeader 
        className="cursor-pointer hover:bg-muted/20 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Methodology & Data Sources
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </CardTitle>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="pt-0">
          <div className="space-y-6">
            {/* Framework Badges */}
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="border-primary/30 text-primary">
                <Shield className="w-3 h-3 mr-1" />
                ISO 10668
              </Badge>
              <Badge variant="outline" className="border-primary/30 text-primary">
                <BarChart3 className="w-3 h-3 mr-1" />
                BSI-Aligned
              </Badge>
              <Badge variant="outline" className="border-primary/30 text-primary">
                <TrendingUp className="w-3 h-3 mr-1" />
                Interbrand-Aligned
              </Badge>
            </div>
            
            {/* Weight Distribution */}
            <div>
              <h4 className="text-sm font-medium mb-3">Weight Distribution</h4>
              <div className="space-y-2">
                {weights.map((w, i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>{w.label}</span>
                      <span className="font-medium">{w.value}%</span>
                    </div>
                    <Progress value={w.value} className="h-1.5" />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Data Translation Preview */}
            <div>
              <h4 className="text-sm font-medium mb-3">Sample Signal Translation</h4>
              <div className="text-xs space-y-1 text-muted-foreground">
                <p>Instagram 3.2M followers → Digital Reach Score: 85/100</p>
                <p>Nations League Champion → Tournament Factor: +12 pts</p>
                <p>Host Country Status → Market Premium: 1.4x multiplier</p>
              </div>
            </div>
            
            <Link to="/methodology">
              <Button variant="outline" size="sm" className="w-full">
                View Full Methodology
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Main US Soccer Portal Component
export default function USSoccerPortal() {
  const [usmnt, setUsmnt] = useState(null);
  const [uswnt, setUswnt] = useState(null);
  const [verifiedSocialData, setVerifiedSocialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCycle, setSelectedCycle] = useState('men');
  const [activeTab, setActiveTab] = useState('overview');
  const { isDemoMode, toggleDemoMode } = useDemoMode();

  // Load team data
  useEffect(() => {
    async function loadTeams() {
      setLoading(true);
      try {
        const [usmntRes, uswntRes] = await Promise.all([
          authFetch('/api/national-team/usmnt'),
          authFetch('/api/national-team/uswnt')
        ]);
        if (!usmntRes.ok || !uswntRes.ok) {
          setUsmnt(null); setUswnt(null); setLoading(false); return;
        }
        const usmntData = await usmntRes.json();
        const uswntData = await uswntRes.json();
        setUsmnt(usmntData);
        setUswnt(uswntData);
      } catch (e) {
        console.error('Error loading teams:', e);
      }
      setLoading(false);
    }
    loadTeams();
  }, []);

  // Load verified social data when demo mode changes
  useEffect(() => {
    async function loadVerifiedData() {
      try {
        const res = await authFetch(`/api/us-soccer/verified-social?demo_mode=${isDemoMode}`);
        if (!res.ok) { setVerifiedSocialData(null); return; }
        const data = await res.json();
        setVerifiedSocialData(data);
      } catch (e) {
        console.error('Error loading verified social data:', e);
        setVerifiedSocialData(null);
      }
    }
    loadVerifiedData();
  }, [isDemoMode]);

  const handleExportPDF = () => {
    trackEvent('export_pdf_click', { report: 'us-soccer' });
    const reportData = {
      usmnt,
      uswnt,
      verifiedSocialData,
      isDemoMode,
    };
    
    generatePDFReport(reportData, 'us-soccer');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading US Soccer intelligence...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="us-soccer-portal">
      <Header />
      
      {/* Portal Header */}
      <div className="border-b border-border bg-gradient-to-r from-primary/5 via-card to-stable/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Title Section */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className="bg-stable/10 text-stable border-stable/30 border text-xs">Live</Badge>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
                    US Soccer Brand Intelligence Portal
                  </h1>
                  <p className="text-sm text-muted-foreground">Powered by YBV</p>
                </div>
              </div>
              <p className="text-muted-foreground max-w-xl mt-2">
                Executive overview of the combined US Soccer ecosystem. Consolidated brand intelligence for federation-level decision making.
              </p>
            </div>
            
            {/* Data Status & Controls */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <DataIntegrityBadge status={isDemoMode ? 'simulated' : 'verified'} />
                <LastUpdated />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Strip — always visible above tabs */}
      <div className="border-b border-border/30 bg-card/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          {/* Hero Metric */}
          <div className="mb-4">
            <p className="text-lg sm:text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              {formatCurrency((usmnt?.economic_value?.range_low || 240000000) + (uswnt?.economic_value?.range_low || 165000000))} – {formatCurrency((usmnt?.economic_value?.range_high || 340000000) + (uswnt?.economic_value?.range_high || 230000000))}
            </p>
            <p className="text-2xs text-muted-foreground uppercase tracking-wider mt-1">Combined Brand Value</p>
            <p className="text-2xs text-muted-foreground/50 mt-0.5">Estimated current combined valuation range</p>
          </div>
          {/* Supporting Metrics */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-3 border-t border-border/20">
            <div className="flex items-baseline gap-1.5">
              <span className="text-sm font-semibold text-muted-foreground">2</span>
              <span className="text-2xs text-muted-foreground/60 uppercase tracking-wider">Active Programs</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-sm font-semibold text-muted-foreground">52</span>
              <span className="text-2xs text-muted-foreground/60 uppercase tracking-wider">Players Tracked</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-sm font-semibold text-stable/80">+{usmnt?.economic_value?.year_over_year_change || 18}%</span>
              <span className="text-2xs text-muted-foreground/60 uppercase tracking-wider">USMNT YoY Growth</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <DashboardTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-10 space-y-8">

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <section>
              <h2 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                Federation Overview
              </h2>
              <p className="text-sm text-muted-foreground mb-5">Consolidated brand intelligence for USMNT and USWNT programs</p>
              <div className="grid md:grid-cols-2 gap-6">
                <TeamSummaryCard team={usmnt} />
                <TeamSummaryCard team={uswnt} />
              </div>
              <Card className="mt-4 bg-gradient-to-r from-primary/5 to-stable/5 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Combined Federation Brand Value</p>
                      <p className="text-3xl font-bold text-foreground">
                        {formatCurrency((usmnt?.economic_value?.range_low || 240000000) + (uswnt?.economic_value?.range_low || 165000000))} – {formatCurrency((usmnt?.economic_value?.range_high || 340000000) + (uswnt?.economic_value?.range_high || 230000000))}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-foreground">2</p>
                        <p className="text-muted-foreground">Active Programs</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-stable">52</p>
                        <p className="text-muted-foreground">Players Tracked</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            <section data-testid="strategic-interpretation-section">
              <DecisionInsightPanel
                type="strategic-interpretation"
                title="Federation Brand Positioning & Whitespace Opportunity"
                narrative={`Based on the current in-platform dataset, the federation's combined estimated brand value range of ${formatCurrency((usmnt?.economic_value?.range_low || 240000000) + (uswnt?.economic_value?.range_low || 165000000))} to ${formatCurrency((usmnt?.economic_value?.range_high || 340000000) + (uswnt?.economic_value?.range_high || 230000000))} would position U.S. Soccer among the most commercially active federations globally. Modeled benchmarking estimates the current sponsorship portfolio at approximately 78% of top European federation equivalents (modeled baseline), indicating potential whitespace for category depth optimization and new vertical expansion — particularly ahead of the 2026 co-host cycle.`}
                keyPoints={[
                  'USMNT brand trajectory estimated at +' + (usmnt?.economic_value?.year_over_year_change || 18) + '% YoY (modeled baseline), attributed to Nations League success and host-country momentum',
                  'USWNT appears among the top global programs by modeled digital reach among women\'s national teams, though growth appears to have moderated post-Paris 2024',
                  'Dual World Cup cycle exposure (2026 men\'s, 2027 women\'s) represents a potential multi-year sponsorship leverage window',
                  'Estimated sponsorship whitespace of +$20M to +$35M in incremental annual revenue remains directional and subject to federation portfolio validation',
                ]}
                disclaimer="This interpretation is based on the current in-platform dataset, which includes modeled baselines and estimated metrics — not live external feeds. All figures are directional indicators, not certified valuations. Sponsorship estimates are subject to federation-specific commercial data calibration."
              />
            </section>
          </>
        )}

        {/* Drivers Tab */}
        {activeTab === 'drivers' && (
          <>
            <section>
              <DualCycleChart 
                usmnt={usmnt} 
                uswnt={uswnt} 
                selectedCycle={selectedCycle}
                onCycleChange={setSelectedCycle}
              />
            </section>
            <section className="grid lg:grid-cols-2 gap-6">
              <DigitalPresenceComparison 
                usmnt={usmnt} 
                uswnt={uswnt} 
                verifiedData={verifiedSocialData}
                isDemoMode={isDemoMode}
              />
              <StarPlayersComparison usmnt={usmnt} uswnt={uswnt} />
            </section>
          </>
        )}

        {/* Opportunities Tab */}
        {activeTab === 'opportunities' && (
          <section>
            <StrategicInsights usmnt={usmnt} uswnt={uswnt} />
          </section>
        )}

        {/* Scenarios Tab */}
        {activeTab === 'scenarios' && (
          <section className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
                <Layers className="w-5 h-5" />
                World Cup Scenario Modeling
              </h2>
              <p className="text-sm text-muted-foreground mb-5">
                6-stage tournament outcome model calibrated to the expanded 48-team 2026 format.
                Explore detailed financial projections and decision insights in the ROI Simulator.
              </p>
            </div>
            <Card className="bg-card border-border/50">
              <CardContent className="p-5">
                <div className="grid sm:grid-cols-3 gap-4 mb-4">
                  <div className="p-3 rounded-lg bg-muted/20 border border-border/30 text-center">
                    <p className="text-2xs text-muted-foreground uppercase tracking-wider">Scenarios Modeled</p>
                    <p className="text-xl font-bold text-foreground mt-1">6</p>
                    <p className="text-2xs text-muted-foreground">Group Stage → Championship</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/20 border border-border/30 text-center">
                    <p className="text-2xs text-muted-foreground uppercase tracking-wider">Base Case</p>
                    <p className="text-xl font-bold text-primary mt-1">Quarterfinal</p>
                    <p className="text-2xs text-muted-foreground">25% modeled probability</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/20 border border-border/30 text-center">
                    <p className="text-2xs text-muted-foreground uppercase tracking-wider">Includes R32</p>
                    <p className="text-xl font-bold text-stable mt-1">Yes</p>
                    <p className="text-2xs text-muted-foreground">48-team format aligned</p>
                  </div>
                </div>
                <Link to="/us-soccer/roi-simulator">
                  <Button className="w-full bg-primary hover:bg-primary/90" data-testid="open-roi-simulator">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Open ROI Simulator
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Export Tab */}
        {activeTab === 'export' && (
          <>
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <FileDown className="w-5 h-5" />
                Export & Documentation
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <Card className="bg-card border-border/50">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-semibold text-foreground mb-2">Strategic Report</h3>
                    <p className="text-xs text-muted-foreground mb-3">Full US Soccer intelligence report with watermark and timestamp.</p>
                    <Button onClick={handleExportPDF} className="w-full bg-primary hover:bg-primary/90" data-testid="export-us-soccer-report">
                      <FileDown className="w-4 h-4 mr-2" />
                      Export Strategic Report
                    </Button>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border/50">
                  <CardContent className="p-5">
                    <h3 className="text-sm font-semibold text-foreground mb-2">Procurement Kit</h3>
                    <p className="text-xs text-muted-foreground mb-3">Methodology, security, implementation plan, and scope documents.</p>
                    <Link to="/us-soccer/procurement-kit">
                      <Button variant="outline" className="w-full" data-testid="procurement-kit-link">
                        <FileDown className="w-4 h-4 mr-2" />
                        Open Procurement Kit
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </div>
            </section>

            <section>
              <MethodologySection />
            </section>

            <Card className="bg-muted/20 border-border">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground text-sm">Platform Notice</p>
                    <p className="text-sm text-muted-foreground">
                      This portal demonstrates strategic brand intelligence capabilities for U.S. Soccer Federation. 
                      Real-time signals from official accounts, media APIs, and federation data can be 
                      integrated upon agreement. Contact us to discuss enterprise data integration.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <FeedbackWidget route="/us-soccer" />
      </div>
      <PlatformFooter />
    </div>
  );
}
